import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Form } from '@nucleus-commerce/ui/components/form';

import {
  ADDRESS_FIELDS,
  AddressFields,
  addressSchema,
  fromAddress,
  toAddressInput,
  type AddressFormInput,
  type AddressFormOutput,
} from '@/features/orders';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { isStateConflict } from '../schemas/draft-order-rules';

export type AddressKind = 'shippingAddress' | 'billingAddress';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(ADDRESS_FIELDS);

/** `shippingAddress.city` lands on `city`. */
const mapField = (field: string) => field.replace(/^(shipping|billing)Address\./, '');

export interface DraftAddressDialogProps {
  storeId: string;
  draft: DraftOrder;
  kind: AddressKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The order address form, saved onto the draft; the billing one can start from shipping. */
export function DraftAddressDialog({
  storeId,
  draft,
  kind,
  open,
  onOpenChange,
}: DraftAddressDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:address.saved');
  const form = useForm<AddressFormInput, unknown, AddressFormOutput>({
    resolver: useI18nResolver(addressSchema),
    defaultValues: fromAddress(draft[kind]),
  });
  const shipping = kind === 'shippingAddress';

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ id: draft.id, body: { [kind]: toAddressInput(values) } });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {shipping
              ? t('draftOrders:address.editShipping')
              : t('draftOrders:address.editBilling')}
          </DialogTitle>
          <DialogDescription>{t('draftOrders:address.editDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            {!shipping && draft.shippingAddress ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => form.reset(fromAddress(draft.shippingAddress))}
              >
                {t('draftOrders:address.copyShipping')}
              </Button>
            ) : null}
            <AddressFields control={form.control} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('draftOrders:address.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
